'use client';

import { uploadFile } from '@/lib/upload';
import { useAuth, useUser } from '@clerk/nextjs';
import { cn } from '@/lib/utils';
import { useReactFlow } from '@xyflow/react';
import { FileIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNodeOperations } from './node-operations';
import { useProject } from './project';
import { env } from '@/lib/env';
import { useI18n } from '@/providers/i18n';
import { textToTiptapJSON } from '@/lib/utils';
import {
  CANVAS_PENDING_INSPIRATION_KEY,
  INSPIRATION_MIME,
  USE_INSPIRATION_EVENT,
  buildInspirationNode,
  measureInspirationDurations,
  type InspirationNodePayload,
} from '@/components/canvas/utils/inspiration-node';
import type { InlineAttachment } from '@flowmuse/models/inline-attachments';

type NodeDropzoneProviderProps = {
  children: ReactNode;
};

export const NodeDropzoneProvider = ({
  children,
}: NodeDropzoneProviderProps) => {
  const { t } = useI18n();
  const { getViewport, screenToFlowPosition, updateNodeData } = useReactFlow();
  const { addNode } = useNodeOperations();
  const project = useProject();
  const { getToken } = useAuth();
  const { user } = useUser();
  const disableDrop = (env.NEXT_PUBLIC_DISABLE_NODE_DROP ?? '').toLowerCase() === 'true';

  const [dragPoint, setDragPoint] = useState<{ x: number; y: number } | null>(
    null
  );

  // Shared by the drop branch and the click-to-use listener (both live in
  // empty-dep effects, hence the ref indirection). Creates the prefilled node
  // immediately, then backfills reference video/audio durations off the
  // critical path (the node-side duration validation reads attachment.duration).
  const addInspirationNode = useCallback(
    (payload: InspirationNodePayload, position: { x: number; y: number }) => {
      const { nodeType, data } = buildInspirationNode(payload);
      const nodeId = addNode(nodeType, {
        position,
        origin: [0, 0],
        selected: true,
        data: data as any,
      });
      if (!nodeId) return;
      void measureInspirationDurations(data.inlineAttachments as InlineAttachment[] | undefined)
        .then((durations) => {
          if (!durations) return;
          updateNodeData(nodeId, (node) => {
            const current = (node.data as any)?.inlineAttachments;
            if (!Array.isArray(current)) return {};
            return {
              inlineAttachments: current.map((a: InlineAttachment) =>
                durations.has(a.id) ? { ...a, duration: durations.get(a.id) } : a
              ),
            };
          });
        })
        .catch(() => {});
    },
    [addNode, updateNodeData]
  );
  const addInspirationNodeRef = useRef(addInspirationNode);
  addInspirationNodeRef.current = addInspirationNode;
  const [assetDragActive, setAssetDragActive] = useState(false);
  const [assetGhost, setAssetGhost] = useState<{ thumb: string; name: string; caption?: string } | null>(null);

  // Show the same "release to upload" ghost immediately on asset drag start,
  // so users don't see the browser's default drag cursor first.
  useEffect(() => {
    const onAssetStart = (e: any) => {
      try {
        const x = Number(e?.detail?.x);
        const y = Number(e?.detail?.y);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          setDragPoint({ x, y });
        }
        const thumb = String(e?.detail?.thumb || '');
        const name = String(e?.detail?.name || '');
        const caption = String(e?.detail?.caption || '') || undefined;
        setAssetGhost(thumb || name ? { thumb, name, caption } : null);
        setAssetDragActive(true);
      } catch {}
    };
    const onAssetEnd = () => {
      try {
        setAssetDragActive(false);
        setDragPoint(null);
        setAssetGhost(null);
      } catch {}
    };
    window.addEventListener('asset-library-drag-start', onAssetStart as EventListener);
    window.addEventListener('asset-library-drag-end', onAssetEnd as EventListener);
    window.addEventListener('workflow-library-drag-start', onAssetStart as EventListener);
    window.addEventListener('workflow-library-drag-end', onAssetEnd as EventListener);
    return () => {
      window.removeEventListener('asset-library-drag-start', onAssetStart as EventListener);
      window.removeEventListener('asset-library-drag-end', onAssetEnd as EventListener);
      window.removeEventListener('workflow-library-drag-start', onAssetStart as EventListener);
      window.removeEventListener('workflow-library-drag-end', onAssetEnd as EventListener);
    };
  }, []);

  // Keep dragPoint synced to real cursor position during asset drag, even when the drawer overlays the canvas.
  useEffect(() => {
    if (!assetDragActive) return;
    let raf = 0;
    const onWinDragOver = (e: DragEvent) => {
      try {
        const x = Number((e as any)?.clientX);
        const y = Number((e as any)?.clientY);
        // Some browsers emit 0,0 for dragover occasionally; ignore to avoid jumping to top-left.
        if (!Number.isFinite(x) || !Number.isFinite(y) || (x === 0 && y === 0)) return;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          setDragPoint({ x, y });
        });
      } catch {}
    };
    window.addEventListener('dragover', onWinDragOver as any, true);
    return () => {
      try {
        if (raf) cancelAnimationFrame(raf);
      } catch {}
      window.removeEventListener('dragover', onWinDragOver as any, true);
    };
  }, [assetDragActive]);

  // Handle dropping library assets at the top level (capture phase) so the drop works even
  // if the drawer overlay still covers part of the canvas when the user releases quickly.
  useEffect(() => {
    const onWinDragOver = (e: DragEvent) => {
      try {
        const types = Array.from((e as any)?.dataTransfer?.types || []);
        if (
          !types.includes('application/x-flowmuse-asset') &&
          !types.includes('application/x-flowmuse-prompt') &&
          !types.includes('application/x-flowmuse-subject') &&
          !types.includes('application/x-flowmuse-workflow-preset') &&
          !types.includes(INSPIRATION_MIME)
        )
          return;
        e.preventDefault();
        // Keep ghost responsive even before React drop handlers run.
        const x = Number((e as any)?.clientX);
        const y = Number((e as any)?.clientY);
        if (Number.isFinite(x) && Number.isFinite(y) && (x !== 0 || y !== 0)) {
          setDragPoint({ x, y });
        }
        setAssetDragActive(true);
      } catch {}
    };
    const onWinDrop = (e: DragEvent) => {
      try {
        const dt = (e as any)?.dataTransfer;
        const rawAsset = dt?.getData?.('application/x-flowmuse-asset') || '';
        const rawPrompt = dt?.getData?.('application/x-flowmuse-prompt') || '';
        const rawSubject = dt?.getData?.('application/x-flowmuse-subject') || '';
        const rawWorkflowPreset = dt?.getData?.('application/x-flowmuse-workflow-preset') || '';
        const rawInspiration = dt?.getData?.(INSPIRATION_MIME) || '';
        if (!rawAsset && !rawPrompt && !rawSubject && !rawWorkflowPreset && !rawInspiration) return;
        e.preventDefault();
        e.stopPropagation();
        setAssetDragActive(false);
        setDragPoint(null);

        const pos = screenToFlowPosition({
          x: Number((e as any)?.clientX) || 0,
          y: Number((e as any)?.clientY) || 0,
        });

        if (rawAsset) {
          const payload = JSON.parse(String(rawAsset || '{}')) as {
            type: 'image' | 'video' | 'audio';
            url: string;
            src?: string;
          };
          if (!payload?.url) return;
          const fileType = payload.type;
          const mimeType =
            fileType === 'image'
              ? 'image/png'
              : fileType === 'video'
                ? 'video/mp4'
                : 'audio/mpeg';
          addNode('upload', {
            position: pos,
            origin: [0, 0],
            selected: true,
            data: {
              content: { url: payload.url, type: mimeType },
              fileType,
              sourceUrl: payload.url,
              ...(payload.src ? { src: payload.src } : {}),
            } as any,
          });
          try {
            window.dispatchEvent(new CustomEvent('asset-library-dropped'));
          } catch {}
          return;
        }

        // Subject library: drop to create a single upload node with subject flag & optional prompt.
        // - mosaic 模式：节点封面 = 拼图 URL
        // - multi 模式：节点封面 = 首图 URL，并在 data.subjectImages 里保留全部原图
        //   下游（视频/图像生成节点）可以读 subjectImages 把多图主体喂给支持多参考图的模型
        if (rawSubject) {
          const payload = JSON.parse(String(rawSubject || '{}')) as {
            url?: string;
            prompt?: string;
            name?: string;
            subject?: boolean;
            mode?: 'mosaic' | 'multi';
            images?: string[];
          };
          const prompt = String(payload?.prompt || '').trim();
          const subjectName = String(payload?.name || '').trim();
          const mode = payload?.mode === 'multi' ? 'multi' : 'mosaic';
          const images = Array.isArray(payload?.images)
            ? payload!.images!.map((u) => String(u || '').trim()).filter(Boolean)
            : [];
          // multi 模式下封面直接用首张原图；mosaic 模式仍用 payload.url（拼图）
          const coverUrl =
            mode === 'multi' && images.length > 0
              ? images[0]
              : String(payload?.url || '').trim();
          if (!coverUrl) return;
          addNode('upload', {
            position: pos,
            origin: [0, 0],
            selected: true,
            data: {
              name: t('Subject: {{name}}', { name: subjectName || t('Subject') }).slice(0, 64),
              content: { url: coverUrl, type: 'image/png' },
              fileType: 'image',
              sourceUrl: coverUrl,
              prompt: prompt || undefined,
              subject: true,
              subjectMode: mode,
              ...(mode === 'multi' && images.length > 0 ? { subjectImages: images } : {}),
            } as any,
          });
          try {
            window.dispatchEvent(new CustomEvent('subject-library-dropped'));
          } catch {}
          return;
        }

        // Prompt library: drop to create a text (prompt) node
        if (rawPrompt) {
          const payload = JSON.parse(String(rawPrompt || '{}')) as { text?: string; title?: string };
          const text = String(payload?.text || '').trim();
          const title = String(payload?.title || '').trim();
          if (!text) return;
          addNode('text', {
            position: pos,
            origin: [0, 0],
            selected: true,
            data: {
              name: t('Prompt: {{name}}', { name: title || t('Prompt') }).slice(0, 64),
              text,
              content: textToTiptapJSON(text) as any,
            } as any,
          });
          try {
            window.dispatchEvent(new CustomEvent('prompt-library-dropped'));
          } catch {}
          return;
        }

        // Inspiration library: drop to create a prefilled, ready-to-run generation node.
        if (rawInspiration) {
          const payload = JSON.parse(String(rawInspiration || '{}')) as InspirationNodePayload;
          if (!payload?.kind) return;
          addInspirationNodeRef.current(payload, pos);
          return;
        }

        // Workflow library: drop to insert preset workflow (handled by canvas)
        if (rawWorkflowPreset) {
          const payload = JSON.parse(String(rawWorkflowPreset || '{}')) as { id?: string };
          const id = String(payload?.id || '').trim();
          if (!id) return;
          try {
            window.dispatchEvent(
              new CustomEvent('flowmuse:insert-workflow-preset', {
                detail: {
                  id,
                  center: pos,
                },
              })
            );
          } catch {}
          try {
            window.dispatchEvent(new CustomEvent('workflow-library-dropped'));
          } catch {}
          return;
        }
      } catch {}
    };
    window.addEventListener('dragover', onWinDragOver as any, true);
    window.addEventListener('drop', onWinDrop as any, true);
    return () => {
      window.removeEventListener('dragover', onWinDragOver as any, true);
      window.removeEventListener('drop', onWinDrop as any, true);
    };
    // screenToFlowPosition/addNode are stable from providers in practice, but keep deps minimal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inspiration panel "Add to canvas" click: create the prefilled node at the center
  // of the visible canvas area (offset right of the 340px slide-out left panel).
  useEffect(() => {
    const onUseInspiration = (e: Event) => {
      try {
        const payload = (e as CustomEvent)?.detail?.payload as InspirationNodePayload | undefined;
        if (!payload?.kind) return;
        const panelWidth = 360;
        const pos = screenToFlowPosition({
          x: (Math.min(panelWidth, window.innerWidth / 2) + window.innerWidth) / 2,
          y: window.innerHeight / 2,
        });
        addInspirationNodeRef.current(payload, pos);
      } catch {}
    };
    window.addEventListener(USE_INSPIRATION_EVENT, onUseInspiration as EventListener);
    return () =>
      window.removeEventListener(USE_INSPIRATION_EVENT, onUseInspiration as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 站外入口(主页灵感库卡片「在画布打开」)一次性接力:新建项目后跳转到本页,这里在挂载时
  // 直接读取并清空 sessionStorage,而不是依赖事件广播——事件监听器要到这个 effect 跑完才注册，
  // 跳转后立即 dispatch 存在监听器还没挂上就丢事件的时序风险，sessionStorage 没有这个问题。
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CANVAS_PENDING_INSPIRATION_KEY);
      if (!raw) return;
      sessionStorage.removeItem(CANVAS_PENDING_INSPIRATION_KEY);
      const payload = JSON.parse(raw) as InspirationNodePayload;
      if (!payload?.kind) return;
      const pos = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      addInspirationNodeRef.current(payload, pos);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropzone = useDropzone({
    noClick: true,
    autoFocus: false,
    noKeyboard: true,
    disabled: !project || disableDrop,
    onDrop: (acceptedFiles, _rejections, evt) => {
      try {
        setDragPoint(null);
      } catch {}

      // Prefer dropping at cursor position; fallback to viewport center.
      let base = (() => {
        try {
          const x = Number((evt as any)?.clientX);
          const y = Number((evt as any)?.clientY);
          if (Number.isFinite(x) && Number.isFinite(y) && (x !== 0 || y !== 0)) {
            return screenToFlowPosition({ x, y });
          }
        } catch {}
        try {
          const viewport = getViewport();
          return {
            x: -viewport.x / viewport.zoom + window.innerWidth / 2 / viewport.zoom,
            y: -viewport.y / viewport.zoom + window.innerHeight / 2 / viewport.zoom,
          };
        } catch {}
        return { x: 0, y: 0 };
      })();

      const gap = 48;
      acceptedFiles.forEach((file, i) => {
        const mimeType = String(file?.type || '');
        let fileType: 'image' | 'video' | 'audio' | undefined;
        if (mimeType.startsWith('image/')) fileType = 'image';
        else if (mimeType.startsWith('video/')) fileType = 'video';
        else if (mimeType.startsWith('audio/')) fileType = 'audio';

        // Drop instant placeholder node first (skeleton), then upload in background.
        const nodeId = addNode('upload', {
          data: {
            uploading: true,
            uploadError: undefined,
            content: {
              // no url yet — upload pending
              type: mimeType,
              name: file.name,
            },
            fileType,
          } as any,
          // Align node top-left with cursor (and the drag ghost)
          origin: [0, 0],
          position: {
            x: base.x,
            y: base.y + i * gap,
          },
          selected: i === 0,
        });

        if (!nodeId) return;

        void (async () => {
          try {
            const token = await getToken({ template: 'supabase' });
            if (!token) throw new Error('No Clerk token');
            if (!user?.id) throw new Error('No Clerk user');

            const uploaded = await uploadFile(file, 'files', {
              token,
              userId: user.id,
              projectId: String((project as any)?.id || ''),
              assetType: fileType,
            });

            const uploadedType = String(uploaded?.type || mimeType);
            let uploadedFileType: 'image' | 'video' | 'audio' | undefined;
            if (uploadedType.startsWith('image/')) uploadedFileType = 'image';
            else if (uploadedType.startsWith('video/')) uploadedFileType = 'video';
            else if (uploadedType.startsWith('audio/')) uploadedFileType = 'audio';

            updateNodeData(nodeId, {
              uploading: false,
              uploadError: undefined,
              content: {
                url: uploaded.url,
                type: uploadedType,
                name: file.name,
              },
              fileType: uploadedFileType,
              sourceUrl: uploaded.url,
            } as any);
          } catch (e: any) {
            updateNodeData(nodeId, {
              uploading: false,
              uploadError: String(e?.message || e || 'Upload failed'),
            } as any);
          }
        })();
      });
    },
  });

  return (
    <div
      {...dropzone.getRootProps({
        onDragEnter: (e: any) => {
          try {
            // keep hook for future enhancements (e.g. show count), but don't block
            void e;
          } catch {}
        },
        onDragOver: (e: any) => {
          try {
            if (disableDrop) return;
            const types = Array.from((e as any)?.dataTransfer?.types || []);
            if (types.includes('application/x-flowmuse-asset')) {
              setAssetDragActive(true);
            }
            setDragPoint({ x: Number(e.clientX) || 0, y: Number(e.clientY) || 0 });
          } catch {}
        },
        onDragLeave: () => {
          try {
            setDragPoint(null);
            setAssetDragActive(false);
          } catch {}
        },
        onDrop: () => {
          try {
            setDragPoint(null);
            setAssetDragActive(false);
          } catch {}
        },
      })}
      className="size-full"
    >
      <input
        {...dropzone.getInputProps()}
        className="pointer-events-none hidden select-none"
      />
      {!disableDrop && (dropzone.isDragActive || assetDragActive) && dragPoint ? (
        <div
          className="pointer-events-none fixed z-[999999]"
          style={{
            // Align ghost top-left to cursor, matching created node position.
            left: dragPoint.x,
            top: dragPoint.y,
          }}
        >
          {assetDragActive ? (
            // Compact ghost for library asset drags: wraps the thumbnail + name
            // together with the "release to upload" caption.
            <div className="flex flex-col gap-1.5 rounded-2xl border bg-background/90 p-2 shadow-lg backdrop-blur">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted/60">
                  {assetGhost?.thumb ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={assetGhost.thumb}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FileIcon size={16} className="text-muted-foreground" />
                  )}
                </span>
                {assetGhost?.name ? (
                  <span className="max-w-[160px] truncate text-xs font-medium text-foreground/90">
                    {assetGhost.name}
                  </span>
                ) : null}
              </div>
              <span className="text-center text-[11px] text-muted-foreground">
                {assetGhost?.caption || t('drop.release_to_upload')}
              </span>
            </div>
          ) : (
            <div className="w-[260px] rounded-lg border bg-background/80 backdrop-blur shadow-xl">
              <div className="p-3">
                {/* Square ghost preview */}
                <div className="relative aspect-square w-full rounded-md bg-muted/60 animate-pulse overflow-hidden">
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border bg-background/75 backdrop-blur">
                      <FileIcon size={18} className="text-muted-foreground" />
                    </div>
                    <div className="text-sm font-medium text-foreground/80">
                      {t('drop.release_to_upload')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
      {children}
    </div>
  );
};
